/**
 * @fileoverview Manual Transactions Hook
 * Handles manual transaction workflows and transaction details modal.
 *
 * The hook owns:
 *   - the open/close state of the shared `TransactionDetailsModal`
 *   - a per-transaction `onConfirmed(txid?)` callback that fires when the
 *     user has either auto-signed in-browser (we know the txid) or
 *     manually clicked "I've copied & broadcast" (txid unknown).
 *
 * Callers (Mint, Transfer, **List for sale**, **Cancel listing**, **Buy**)
 * use the same hook to be notified after the broadcast so they can run the
 * matching `record-*` API call without having to subscribe to lower-level
 * wallet events.
 */

import { useCallback, useRef, useState } from 'react';
import type { TransactionData } from '../services/zcashWallet';

export interface TransactionDetails {
	recipient: string;
	amount: number;
	memo: string;
	description: string;
}

// `txid` is only known on the auto-sign path. Manual broadcast users click
// "I've copied" and we don't yet have the chain id; downstream callers
// should handle that gracefully (typically: optimistically refresh the
// page and let the scanner backfill).
export type TransactionConfirmedCallback = (txid?: string) => void | Promise<void>;

export interface UseManualTransactionsResult {
	// Transaction details modal state
	showTransactionDetails: boolean;
	transactionDetails: TransactionDetails | null;

	// Modal controls
	openTransactionDetailsModal: (
		details: TransactionDetails,
		onConfirmed?: TransactionConfirmedCallback
	) => void;
	closeTransactionDetailsModal: () => void;

	// Manual transaction handler
	handleManualTransaction: (
		txData: TransactionData,
		description: string,
		onConfirmed?: TransactionConfirmedCallback
	) => void;

	// Confirm transaction was copied and sent
	confirmTransactionSent: (txid?: string) => void;
}

export const useManualTransactions = (): UseManualTransactionsResult => {
	const [showTransactionDetails, setShowTransactionDetails] = useState(false);
	const [transactionDetails, setTransactionDetails] = useState<TransactionDetails | null>(null);
	// Stored in a ref because the callback is invoked from inside the
	// modal (descendant of the provider) and we don't want stale-closure
	// versions captured at render time of an unrelated component.
	const onConfirmedRef = useRef<TransactionConfirmedCallback | null>(null);

	const openTransactionDetailsModal = useCallback(
		(details: TransactionDetails, onConfirmed?: TransactionConfirmedCallback) => {
			setTransactionDetails(details);
			setShowTransactionDetails(true);
			onConfirmedRef.current = onConfirmed ?? null;
		},
		[]
	);

	const closeTransactionDetailsModal = useCallback(() => {
		setShowTransactionDetails(false);
		setTransactionDetails(null);
		onConfirmedRef.current = null;
	}, []);

	const handleManualTransaction = useCallback(
		(
			txData: TransactionData,
			description: string,
			onConfirmed?: TransactionConfirmedCallback
		) => {
			const details: TransactionDetails = {
				recipient: txData.recipient,
				amount: txData.amount,
				memo: txData.memo || '',
				description
			};
			openTransactionDetailsModal(details, onConfirmed);
		},
		[openTransactionDetailsModal]
	);

	const confirmTransactionSent = useCallback((txid?: string) => {
		const cb = onConfirmedRef.current;
		closeTransactionDetailsModal();
		if (cb) {
			// Fire and forget — the callback is responsible for its own
			// error reporting (toast etc.); we don't want a failing
			// record-* API call to keep the modal open.
			try {
				const result = cb(txid);
				if (result && typeof (result as Promise<void>).catch === 'function') {
					(result as Promise<void>).catch((err) => {
						// eslint-disable-next-line no-console
						console.error('Transaction confirmed callback failed:', err);
					});
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Transaction confirmed callback threw:', err);
			}
		}
	}, [closeTransactionDetailsModal]);

	return {
		showTransactionDetails,
		transactionDetails,
		openTransactionDetailsModal,
		closeTransactionDetailsModal,
		handleManualTransaction,
		confirmTransactionSent
	};
};

export default useManualTransactions;
